import { warnText } from '../ui';
import { CSSProperties, useEffect, useState } from 'react';
import {
  BoardSelection,
  FILTER_ID_LIST,
  boardLabel,
  equivalentBoardNames,
  formatIds,
  isSelectable,
  parseIds,
  prunedSelection,
  sameSelection,
  soleBoardSelection,
  toggled,
} from './boardSelection';
import { JiraBoard, JiraListings } from './listings';
import { JIRA_PROJECT_KEY } from './presets';

const NO_PROJECT_HINT =
  'Pick a Jira project first, then its boards appear here.';
const MANUAL_HINT =
  'Boards cannot be listed, so the saved-filter ids are entered by hand. Open a board in Jira, then Configure to read its filter id — it is not the board id in the URL.';
const MANUAL_ERROR = 'Numeric saved-filter ids separated by commas.';
const EMPTY_HINT = 'Tick at least one board: an instance must be scoped.';

const container: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginBottom: 14,
  maxWidth: 560,
};

const label: CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--bui-fg-secondary)',
};

const hint: CSSProperties = {
  fontSize: 11.5,
  color: 'var(--bui-fg-secondary)',
};
const errorText: CSSProperties = {
  fontSize: 12,
  color: 'var(--bui-fg-danger)',
};

const row: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid var(--bui-border-1)',
  background: 'var(--bui-bg-neutral-2)',
};

const input = (invalid: boolean): CSSProperties => ({
  fontSize: 13,
  padding: '7px 10px',
  borderRadius: 6,
  border: `1px solid ${
    invalid ? 'var(--bui-fg-danger)' : 'var(--bui-border-2)'
  }`,
  background: 'var(--bui-bg-neutral-1)',
  color: 'var(--bui-fg-primary)',
  width: '100%',
});

type Listing =
  | { kind: 'noProject' }
  | { kind: 'loading' }
  | { kind: 'listed'; boards: JiraBoard[] }
  | { kind: 'projectUnknown' }
  | { kind: 'unavailable'; detail: string };

export const JiraBoardPicker = ({
  idPrefix,
  title,
  projectKey,
  selection,
  onChange,
  listings,
  unavailableReason,
  autoSelectSoleBoard = false,
  error,
}: {
  idPrefix: string;
  title: string;
  projectKey: string;
  selection: BoardSelection;
  onChange: (next: BoardSelection) => void;
  listings: JiraListings;
  unavailableReason?: string;
  autoSelectSoleBoard?: boolean;
  error?: string;
}) => {
  const [listing, setListing] = useState<Listing>({ kind: 'noProject' });
  const [typedFilterIds, setTypedFilterIds] = useState(() =>
    formatIds(selection.filterIds),
  );

  useEffect(() => {
    if (unavailableReason) {
      setListing({ kind: 'unavailable', detail: unavailableReason });
      return undefined;
    }
    if (!JIRA_PROJECT_KEY.test(projectKey)) {
      setListing({ kind: 'noProject' });
      return undefined;
    }
    let current = true;
    setListing({ kind: 'loading' });
    listings.boardsOf(projectKey).then(outcome => {
      if (!current) return;
      if (outcome.status === 'listed') {
        setListing({ kind: 'listed', boards: outcome.boards });
        return;
      }
      setListing(
        outcome.status === 'projectUnknown'
          ? { kind: 'projectUnknown' }
          : { kind: 'unavailable', detail: outcome.detail },
      );
    });
    return () => {
      current = false;
    };
  }, [projectKey, unavailableReason, listings]);

  useEffect(() => {
    if (listing.kind !== 'listed') return;
    const empty =
      selection.boardIds.length === 0 && selection.filterIds.length === 0;
    const next =
      empty && autoSelectSoleBoard
        ? soleBoardSelection(listing.boards)
        : prunedSelection(listing.boards, selection);
    if (!sameSelection(next, selection)) onChange(next);
  }, [listing, selection, autoSelectSoleBoard, onChange]);

  const onTypeFilterIds = (raw: string) => {
    setTypedFilterIds(raw);
    if (FILTER_ID_LIST.test(raw.trim())) {
      onChange({ boardIds: [], filterIds: parseIds(raw) });
    }
  };

  const boards = listing.kind === 'listed' ? listing.boards : [];
  const ticked = new Set(selection.boardIds);

  return (
    <div style={container}>
      <span style={label}>{title}</span>

      {listing.kind === 'noProject' && (
        <span style={hint}>{NO_PROJECT_HINT}</span>
      )}
      {listing.kind === 'loading' && (
        <span style={hint}>Listing the boards of {projectKey}…</span>
      )}

      {listing.kind === 'projectUnknown' && (
        <span role="alert" style={errorText}>
          Jira does not know a project {projectKey}, so it has no board to
          offer. Check the key.
        </span>
      )}

      {listing.kind === 'listed' && boards.length === 0 && (
        <span role="alert" style={errorText}>
          Jira lists no board on {projectKey}. Only a software project has
          boards, so this project cannot carry a scoped instance.
        </span>
      )}

      {boards.map(board => {
        const equivalent = equivalentBoardNames(board, boards);
        return (
          <div key={board.id} style={row}>
            <input
              type="checkbox"
              id={`${idPrefix}-board-${board.id}`}
              aria-label={boardLabel(board)}
              checked={ticked.has(board.id)}
              disabled={!isSelectable(board)}
              onChange={() => onChange(toggled(boards, selection, board.id))}
            />
            <label
              htmlFor={`${idPrefix}-board-${board.id}`}
              style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <span style={{ fontSize: 13 }}>{boardLabel(board)}</span>
              <span style={hint}>{board.type}</span>
              {!isSelectable(board) && (
                <span style={warnText}>
                  Its saved filter cannot be read with the platform credential,
                  so it cannot be used as a scope.
                </span>
              )}
              {equivalent.length > 0 && (
                <span style={warnText}>
                  Same filter as {equivalent.join(', ')}: picking either gives
                  the same scope.
                </span>
              )}
            </label>
          </div>
        );
      })}

      {listing.kind === 'unavailable' && (
        <>
          <span style={warnText}>
            {unavailableReason ??
              `Jira could not be listed (${listing.detail}).`}
          </span>
          <span style={hint}>{MANUAL_HINT}</span>
          <input
            id={`${idPrefix}-filter-ids`}
            aria-label="Jira saved-filter ids"
            style={input(!FILTER_ID_LIST.test(typedFilterIds.trim()))}
            value={typedFilterIds}
            onChange={event => onTypeFilterIds(event.target.value)}
          />
          {!FILTER_ID_LIST.test(typedFilterIds.trim()) && (
            <span role="alert" style={errorText}>
              {MANUAL_ERROR}
            </span>
          )}
        </>
      )}

      {listing.kind === 'listed' &&
        selection.filterIds.length > 0 &&
        selection.boardIds.length === 0 && (
          <span style={warnText}>
            This instance is scoped by filter ids that no board here was matched
            to. Ticking a board replaces them.
          </span>
        )}

      {selection.filterIds.length > 0 ? (
        <span style={hint}>
          Scope: filter IN ({selection.filterIds.join(', ')})
        </span>
      ) : (
        listing.kind === 'listed' &&
        boards.length > 0 && <span style={hint}>{EMPTY_HINT}</span>
      )}

      {error && (
        <span role="alert" style={errorText}>
          {error}
        </span>
      )}
    </div>
  );
};
